import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List, fromJS } from 'immutable'
import _get from 'lodash/get'
import _round from 'lodash/round'
import _isFunction from 'lodash/isFunction'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../actions/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { get as getConfig } from '../../../config/index'
import { H4, H6 } from '../../Heading/index'
import Button from '../../Button/index'
import {
  AUTHENTICATED_PLAN_SKUS,
  formatPrice,
  PLAN_SUBSCRIPTION_CANCELLED,
  PLAN_TYPE_UPGRADE,
  PLAN_TYPE_DOWNGRADE,
  PLAN_ID_COMPLIMENTARY,
  determinePlanUpgradeDowngrade,
  determinePlanUpgradeDowngradeV3,
  getActiveProductPlanRateId,
} from '../../../services/plans/index'
import {
  format as formatDateTime,
  getDateTime,
  getDateLocale,
  ordinalDateFormatingi18n,
  formatWithLocale,
  getCurrentTime,
} from '../../../services/date-time/index'
import {
  URL_ACCOUNT,
  URL_ACCOUNT_UPDATE_PAYMENT,
} from '../../../services/url/constants'
import { hasScheduledPause, getUserAccountChangePlanProratePrice } from '../../../services/user-account/index'
import { GIFT_SUBSCRIPTION_STATE } from '../../../services/gift/index'
import { formatTracking, CHECKOUT_ORDER_ERROR_TYPE_PAYMENT } from '../../../services/checkout/index'
import { getPrimary } from '../../../services/languages/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])
const changePlanProratePriceActive = !!_get(config, ['features', 'userAccount', 'changePlanProratePrice'])

function MyAccountChangePlanModal (props) {
  const {
    staticText,
    dismissModal,
    dismissModalOverride,
    updateUserAccountPlan,
    resetAccountChangePlanData,
    userAccount,
    plans,
    user,
    auth,
    currentPlan = Map(),
  } = props

  // Local state for prorate price data
  const [proratePriceData, setProratePriceData] = useState(null)
  const [isProratePriceLoading, setIsProratePriceLoading] = useState(false)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const currentPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  let plansData = List()

  if (!planGraphQLActive) {
    plansData = plans.getIn(['data', 'plans'], List()) || List()
  }

  const selectedPlanSku = plans.getIn(['changePlanData', 'selectedPlan'])
  let selectedPlan = selectedPlanSku ? plans.getIn(['data', 'plans'], List()).find((plan) => {
    return plan.get('sku') === selectedPlanSku
  }) : Map()

  if (planGraphQLActive) {
    selectedPlan = plans.getIn(['changePlanData', 'selectedPlan'])
  }

  const planType = selectedPlan.get('heading')
  const currency = selectedPlan.get('currencyIso')
  const cost = (selectedPlan.get('costs', List()) || List()).last()
  const selectedPlanId = selectedPlan.get('id') || null
  const currentPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const nextPlanId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'productRatePlanId'])
  const isInComp = currentPlanId === PLAN_ID_COMPLIMENTARY
  const inTrialSegment = !!userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'inTrialSegment'])
  const freeTrialNoBilling = !!user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])
  const giftSubscriptionActive = userAccount.getIn(['details', 'data', 'gift', 'status']) === GIFT_SUBSCRIPTION_STATE.ACTIVE
  const useNonProratedChargeMessaging = isInComp
    || inTrialSegment
    || freeTrialNoBilling
    || giftSubscriptionActive
  const planId = isInComp ? nextPlanId : currentPlanId
  const userCurrentRatePlanId = getActiveProductPlanRateId(userAccount)
  const userCurrentPlan = planGraphQLActive ? currentPlan : plansData.find((p) => p.get('id') === userCurrentRatePlanId) || Map()
  const planUpgradeDownGradeType = planGraphQLActive
    ? determinePlanUpgradeDowngradeV3(proratePriceData || Map())
    : determinePlanUpgradeDowngrade(selectedPlanId, planId)
  const isScheduledPause = hasScheduledPause(userAccount)
  const cancelDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'cancelDate'])
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const today = getCurrentTime()
  const cancellationScheduled = cancelDate && (new Date(endDate) >= today)
  const subscriptionStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  let nextBillDate = null
  const planHeading = selectedPlan.get('heading')
  const planIsCurrentPlan = userCurrentRatePlanId === selectedPlanId
  const userPlanIsLegacyStandardPlan = currentPlanSku
    && AUTHENTICATED_PLAN_SKUS.includes(currentPlanSku)
  const nextCharge = (userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'charges'], List()) || List()).last() || Map()
  const nextChargeAmount = nextCharge.get('price')
  let userCurrentPlanCost = cost

  // If the person is in a legacy plan and this is their current plan
  if (planIsCurrentPlan && userPlanIsLegacyStandardPlan) {
    userCurrentPlanCost = nextChargeAmount
  }

  // last ditch effort, just show a price
  if (!userCurrentPlanCost) {
    userCurrentPlanCost = (userCurrentPlan.get('costs', List()) || List()).last() || 0
  }

  if (subscriptionStatus === PLAN_SUBSCRIPTION_CANCELLED) {
    nextBillDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  } else {
    nextBillDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextReviewDate'])
  }

  const reformattedDate = getDateTime(nextBillDate)
  const dateFormatString = ordinalDateFormatingi18n(userLanguage)
  const dateLocale = getDateLocale(userLanguage)
  const formattedDate = formatWithLocale(
    formatDateTime(reformattedDate),
    dateFormatString,
    { locale: dateLocale },
  )
  const isProcessing = userAccount.getIn(['billingSubscriptionsProcessing']) || auth.get('processing')
  const showChangeError = userAccount.getIn(['data', 'billing', 'planChange', '_dataError'])
  || userAccount.getIn(['data', 'billing', 'planChange', 'success']) === false
  const errorCode = userAccount.getIn(['data', 'billing', 'planChange', 'errorCode'])
  const showPlanChanged = userAccount.getIn(['data', 'billing', 'planChange']) && !showChangeError

  useEffect(() => {
    const fetchProratePrice = async () => {
      if (
        changePlanProratePriceActive
        && !isProratePriceLoading
        && !proratePriceData
      ) {
        setIsProratePriceLoading(true)

        try {
          const res = await getUserAccountChangePlanProratePrice({
            auth,
            nextRatePlanId: selectedPlanId,
          })
          setProratePriceData(fromJS(res))
        } catch (error) {
          // Set error state so we can fall back to default description
          setProratePriceData(fromJS({
            error: true,
            success: false,
            amountWithoutTax: null,
            taxAmount: null,
            targetDate: null,
            activateImmediately: false,
          }))
        } finally {
          setIsProratePriceLoading(false)
        }
      } else {
        // Reset state when not upgrading
        setProratePriceData(null)
        setIsProratePriceLoading(false)
      }
    }

    fetchProratePrice()
  }, [changePlanProratePriceActive, auth, selectedPlanId])

  useEffect(() => {
    // Reset the account change plan data when the component unmounts
    return () => {
      resetAccountChangePlanData()
    }
  }, [])

  const renderDescriptionPlaceholder = () => {
    return (
      <div className="my-account-change-plan-modal__container">
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
        <div className="my-account-change-plan-modal__desciption-placeholder" />
      </div>
    )
  }

  const renderErrorState = () => {
    if (errorCode === CHECKOUT_ORDER_ERROR_TYPE_PAYMENT) {
      return (
        <React.Fragment>
          <H4 className="my-account-change-plan-modal__title my-account-change-plan-modal__title--error">{staticText.getIn(['data', 'paymentError'])}</H4>
          <div className="my-account-change-plan-modal__container">
            <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'updateErrorPaymentMethodBody'])} </p>
          </div>
          <Button
            className="button button--primary my-account-change-plan-modal__button"
            url={URL_ACCOUNT_UPDATE_PAYMENT}
          >
            {staticText.getIn(['data', 'updatePaymentMethod'])}
          </Button>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        <H4 className="my-account-change-plan-modal__title my-account-change-plan-modal__title--error">{staticText.getIn(['data', 'error'])}</H4>
        <div className="my-account-change-plan-modal__container">
          <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'updateErrorGeneralBody'])}</p>
        </div>
        <Button
          className="button button--primary my-account-change-plan-modal__button"
          disabled={isProcessing}
          onClick={() => {
            onClickChangePlanConfirm()
          }}
        >
          {isProcessing ? staticText.getIn(['data', 'changePlanButtonProcessing']) : staticText.getIn(['data', 'retry'])}
        </Button>
        <Button
          className="button button--secondary my-account-change-plan-modal__button"
          onClick={() => {
            dismissModal()
          }}
        >
          {staticText.getIn(['data', 'cancel'])}
        </Button>
      </React.Fragment>
    )
  }

  const renderSuccessState = () => {
    return (
      <React.Fragment>
        <H4 className="my-account-change-plan-modal__title">{staticText.getIn(['data', 'success'])}</H4>
        <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'successDescriptionBefore'])} {planHeading} {staticText.getIn(['data', 'plan'])} </p>
        <Button
          className="button button--primary my-account-change-plan-modal__button"
          url={URL_ACCOUNT}
          scrollToTop
          onClick={() => resetAccountChangePlanData()}
        >
          {staticText.getIn(['data', 'goToMyAccount'])}
        </Button>
      </React.Fragment>
    )
  }

  const renderDescription = () => {
    const upgradeLeadInBeforeToday = useNonProratedChargeMessaging
      ? staticText.getIn(['data', 'changeDescriptionImmediateChargeBefore'])
      : staticText.getIn(['data', 'changeDescriptionProratedBefore'])
    const upgradeLeadInBeforePrice = useNonProratedChargeMessaging
      ? staticText.getIn(['data', 'changeDescriptionImmediateChargeBefore'])
      : staticText.getIn(['data', 'changeDescriptionProratedBefore2'])

    const genericUpgradeMessage = (
      <React.Fragment>
        <p className="my-account-change-plan-modal__desciption">
          {upgradeLeadInBeforeToday} <strong>{staticText.getIn(['data', 'today'])} </strong> {staticText.getIn(['data', 'changeDescriptionProratedAfter'])}
        </p>
        {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
      </React.Fragment>
    )

    if (isProratePriceLoading) {
      return renderDescriptionPlaceholder()
    }

    if (planUpgradeDownGradeType === PLAN_TYPE_UPGRADE) {
      if (changePlanProratePriceActive) {
        // If we have successful prorate data, show prorated description
        if (Map.isMap(proratePriceData) && proratePriceData.get('success') && !proratePriceData.get('error')) {
          const proratePrice = proratePriceData.get('amountWithoutTax') || 0
          const prorateTaxAmount = proratePriceData.get('taxAmount') || 0

          // if the user is lapsed, Zuora will return a prorate price of 0
          // so we have to be very generic here
          if (proratePriceData.get('amountWithoutTax') === 0) {
            return genericUpgradeMessage
          }

          return (
            <React.Fragment>
              <p className="my-account-change-plan-modal__desciption">
                {upgradeLeadInBeforePrice} {formatPrice(currency, _round(proratePrice + prorateTaxAmount, 2), false, null, userLanguage)} <strong>{staticText.getIn(['data', 'today'])} </strong> {staticText.getIn(['data', 'changeDescriptionProratedAfter'])}
              </p>
              {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
            </React.Fragment>
          )
        }

        // If API failed or no data, fall back to default description
        return genericUpgradeMessage
      }

      return genericUpgradeMessage
    }

    // If there is a cancel scheduled and this is the user's current plan
    if (cancellationScheduled && currentPlanId === selectedPlanId) {
      return (
        <p
          className="my-account-change-plan-modal__desciption"
        >
          {`${staticText.getIn(['data', 'timeRemaining'])} ${formattedDate}.`}
        </p>
      )
    }

    // Default fallback description for all other cases
    return (
      <React.Fragment>
        <p className="my-account-change-plan-modal__desciption">{staticText.getIn(['data', 'changeDescription'])}</p>
        {isScheduledPause && <p>{staticText.getIn(['data', 'scheduledPauseCancelled'])}</p>}
      </React.Fragment>
    )
  }

  const renderPlanContent = () => {
    return (
      <React.Fragment>
        <H4
          className="my-account-change-plan-modal__title"
        >
          {
            cancellationScheduled
              ? staticText.getIn(['data', 'confirmSelection'])
              : staticText.getIn(['data', 'confirmPlanChangeTitle'])
          }
        </H4>
        <div className="my-account-change-plan-modal__container">
          <H6 className="my-account-change-plan-modal__text">
            {staticText.getIn(['data', 'newPlan'])} {planType}
          </H6>
          <H6 className="my-account-change-plan-modal__text">
            {`${staticText.getIn(['data', 'price'])} ${selectedPlan.get('shortDetails')}`}
          </H6>
          <div className="my-account-change-plan-modal__container">
            {renderDescription()}
          </div>
          {
            !isProcessing
              && !isProratePriceLoading
              && planUpgradeDownGradeType === PLAN_TYPE_DOWNGRADE
              ? <div className="my-account-change-plan-modal__container">
                  <p className="my-account-change-plan-modal__desciption"> {staticText.getIn(['data', 'nextBillDate'])} <strong>{formattedDate}</strong></p>
                </div>
              : null
          }
        </div>
      </React.Fragment>
    )
  }

  const onClickChangePlanConfirm = () => {
    const { inboundTracking } = props
    const tracking = formatTracking(inboundTracking)
    return updateUserAccountPlan({
      nextRatePlanId: selectedPlanId,
      planSku: selectedPlan.get('sku'),
      auth,
      tracking,
      planUpgradeDownGradeType,
    })
  }

  const renderModalDefault = () => {
    return (
      <React.Fragment>
        {renderPlanContent()}
        <Button
          className="button--primary my-account-change-plan-modal__button"
          disabled={isProcessing}
          onClick={() => {
            onClickChangePlanConfirm()
          }}
        >
          {isProcessing ? staticText.getIn(['data', 'changePlanButtonProcessing']) : staticText.getIn(['data', 'confirmChanges'])}
        </Button>
        { !isProcessing
          ? <Button
            className="button--secondary my-account-change-plan-modal__button "
            onClick={
              () => {
                if (dismissModalOverride && _isFunction(dismissModalOverride)) {
                  dismissModalOverride()
                } else {
                  dismissModal()
                }
              }
            }
          >
            {staticText.getIn(['data', 'cancel'])}
          </Button>
          : null
        }
      </React.Fragment>
    )
  }

  const renderChangePlanModal = () => {
    if (showPlanChanged) {
      return renderSuccessState()
    } if (showChangeError) {
      return renderErrorState()
    }
    return renderModalDefault()
  }

  return (
    <div className="my-account-change-plan-modal">
      {renderChangePlanModal()}
    </div>
  )
}

MyAccountChangePlanModal.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  updateUserAccountPlan: PropTypes.func.isRequired,
  dismissModalOverride: PropTypes.func,
}

export default compose(
  connectStaticText({ storeKey: 'myAccountChangePlanModal' }),
  connectRedux(
    (state) => ({
      plans: state.plans,
      userAccount: state.userAccount,
      inboundTracking: state.inboundTracking,
      auth: state.auth,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        dismissModal: actions.dialog.dismissModal,
        updateUserAccountPlan: actions.userAccount.updateUserAccountPlan,
        resetAccountChangePlanData: actions.userAccount.resetAccountChangePlanData,
      }
    },
  ),
)(MyAccountChangePlanModal)
